import { useState } from "react";
import { T, Badge, Card, Btn, StatCard, fmtCcy, font, mono, ST } from '../theme.js';
import { AlertStatus } from '../engines/monitoring-engine.js';


const MONITORING_NAV = [
  { id: 'dashboard', label: 'Alert Dashboard' },
  { id: 'queue', label: 'Review Queue' },
  { id: 'customers', label: 'Customer Risk' },
];

function MonitoringModule({ monitoring, kyc, refresh: parentRefresh }) {
  const [view, setView] = useState('dashboard');
  const [tick, setTick] = useState(0);
  const refresh = () => { setTick(t => t + 1); if (parentRefresh) parentRefresh(); };

  return (
    <div>
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: `1px solid ${T.border}`, overflowX: 'auto' }}>
        {MONITORING_NAV.map(n => (
          <button key={n.id} onClick={() => setView(n.id)} style={{
            padding: '8px 14px', fontSize: 12, fontWeight: view === n.id ? 600 : 400,
            color: view === n.id ? T.blue : T.textSecondary,
            background: 'transparent', border: 'none',
            borderBottom: view === n.id ? `2px solid ${T.blue}` : '2px solid transparent',
            cursor: 'pointer', fontFamily: font, marginBottom: -1, whiteSpace: 'nowrap',
          }}>
            {n.label}
          </button>
        ))}
      </div>

      {view === 'dashboard' && <AlertDashboard monitoring={monitoring} kyc={kyc} key={tick} />}
      {view === 'queue' && <ReviewQueue monitoring={monitoring} kyc={kyc} refresh={refresh} key={tick} />}
      {view === 'customers' && <CustomerRisk monitoring={monitoring} kyc={kyc} key={tick} />}
    </div>
  );
}


// ============================================================
// ALERT DASHBOARD — aggregate overview
// ============================================================

function AlertDashboard({ monitoring, kyc }) {
  const allAlerts = monitoring.alerts || [];
  const open = allAlerts.filter(a => a.status === AlertStatus.OPEN);
  const underReview = allAlerts.filter(a => a.status === AlertStatus.UNDER_REVIEW);
  const escalated = allAlerts.filter(a => a.status === AlertStatus.ESCALATED);
  const closed = allAlerts.filter(a => a.status === AlertStatus.CLOSED_FALSE_POSITIVE || a.status === AlertStatus.CLOSED_GENUINE);
  const high = open.filter(a => a.severity === 'HIGH' || a.severity === 'CRITICAL');

  // Alert type breakdown
  const typeCounts = {};
  for (const a of allAlerts) typeCounts[a.type] = (typeCounts[a.type] || 0) + 1;

  // Customer alert counts
  const custAlerts = {};
  for (const a of open) custAlerts[a.customerId] = (custAlerts[a.customerId] || 0) + 1;

  const customers = kyc ? kyc.getAllCustomers() : [];
  const getCustomerName = id => customers.find(c => c.id === id)?.name || id;

  return (
    <div>
      <ST>Transaction Monitoring Overview</ST>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Open Alerts" value={open.length} sub={`${high.length} high severity`} color={T.red} />
        <StatCard label="Under Review" value={underReview.length} color={T.amber} />
        <StatCard label="Escalated" value={escalated.length} sub="Requires compliance" color={T.purple} />
        <StatCard label="Resolved" value={closed.length} color={T.green} />
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        {/* Alert type breakdown */}
        <Card style={{ flex: 1 }}>
          <ST>Alert Types</ST>
          {Object.keys(typeCounts).length === 0
            ? <div style={{ color: T.textMuted, fontSize: 13, padding: 12, textAlign: 'center' }}>No alerts generated yet</div>
            : Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
              <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${T.borderLight}`, fontSize: 12 }}>
                <span style={{ color: T.text }}>{type.replace(/_/g, ' ')}</span>
                <span style={{ fontWeight: 600, fontFamily: mono, color: T.textSecondary }}>{count}</span>
              </div>
            ))
          }
        </Card>

        {/* Customers with most open alerts */}
        <Card style={{ flex: 1 }}>
          <ST>Customers by Open Alerts</ST>
          {Object.keys(custAlerts).length === 0
            ? <div style={{ color: T.textMuted, fontSize: 13, padding: 12, textAlign: 'center' }}>No open alerts</div>
            : Object.entries(custAlerts).sort((a, b) => b[1] - a[1]).map(([custId, count]) => (
              <div key={custId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${T.borderLight}`, fontSize: 12 }}>
                <span style={{ color: T.text }}>{getCustomerName(custId)}</span>
                <Badge color={count >= 3 ? 'red' : count >= 2 ? 'amber' : 'blue'}>{count}</Badge>
              </div>
            ))
          }
        </Card>
      </div>

      {/* Recent alerts */}
      <Card>
        <ST>Recent Alerts</ST>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>{['Type', 'Severity', 'Customer', 'Status', 'Detail', 'Created'].map((h, i) => <th key={i} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {allAlerts.length === 0
                ? <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: T.textMuted }}>No alerts. Monitoring runs generate alerts when transaction patterns deviate from expected profiles.</td></tr>
                : [...allAlerts].reverse().slice(0, 20).map((a, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                    <td style={{ padding: '6px 8px', fontWeight: 500 }}>{a.type.replace(/_/g, ' ')}</td>
                    <td style={{ padding: '6px 8px' }}><Badge color={a.severity === 'HIGH' || a.severity === 'CRITICAL' ? 'red' : a.severity === 'MEDIUM' ? 'amber' : 'blue'}>{a.severity}</Badge></td>
                    <td style={{ padding: '6px 8px', color: T.textSecondary }}>{getCustomerName(a.customerId)}</td>
                    <td style={{ padding: '6px 8px' }}><Badge color={a.status === AlertStatus.OPEN ? 'red' : a.status === AlertStatus.ESCALATED ? 'purple' : 'gray'}>{a.status.replace(/_/g, ' ')}</Badge></td>
                    <td style={{ padding: '6px 8px', fontSize: 11, color: T.textMuted, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{alertSummary(a)}</td>
                    <td style={{ padding: '6px 8px', fontSize: 10, color: T.textMuted, fontFamily: mono }}>{new Date(a.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function alertSummary(a) {
  if (a.details?.metric) return `${a.details.metric}: expected ${fmtCcy(a.details.expected)}, actual ${fmtCcy(a.details.actual)} (${a.details.deviationPct}%)`;
  if (a.details?.amount) return `${fmtCcy(a.details.amount)} ${a.details.direction} (${a.details.pctOfMonthly}% of monthly)`;
  if (a.details?.unexpectedScheme) return `${a.details.unexpectedScheme}: ${a.details.pctOfTotal}% of txns (expected: ${a.details.expectedSchemes.join(', ')})`;
  if (a.details?.daysSinceLastTransaction) return `${a.details.daysSinceLastTransaction} days since last transaction`;
  if (a.details?.inboundAmount) return `${fmtCcy(a.details.inboundAmount)} in → ${fmtCcy(a.details.outboundWithinWindow)} out within ${a.details.windowHours}h`;
  return JSON.stringify(a.details || {}).slice(0, 80);
}


// ============================================================
// REVIEW QUEUE — RM-focused alert triage
// ============================================================

function ReviewQueue({ monitoring, kyc, refresh }) {
  const allAlerts = monitoring.alerts || [];
  const open = allAlerts.filter(a => a.status === AlertStatus.OPEN || a.status === AlertStatus.UNDER_REVIEW);
  const [selAlert, setSelAlert] = useState(null);
  const [notes, setNotes] = useState('');
  const [filter, setFilter] = useState('all');

  const customers = kyc ? kyc.getAllCustomers() : [];
  const getCustomerName = id => customers.find(c => c.id === id)?.name || id;

  const filtered = filter === 'all' ? open
    : filter === 'high' ? open.filter(a => a.severity === 'HIGH' || a.severity === 'CRITICAL')
    : open.filter(a => a.severity === filter.toUpperCase());

  const handleReview = async (alertId, status) => {
    await monitoring.reviewAlert(alertId, { reviewedBy: 'rm_sarah_j', status, notes });
    setSelAlert(null);
    setNotes('');
    await refresh();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <ST>Alert Review Queue ({open.length} pending)</ST>
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'high', 'medium', 'low'].map(f => (
            <Btn key={f} small variant={filter === f ? 'primary' : 'secondary'} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </Btn>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: T.textSecondary }}>
            {open.length === 0 ? 'No pending alerts — queue is clear' : `No ${filter} severity alerts`}
          </div>
        </Card>
      ) : (
        filtered.sort((a, b) => {
          const sev = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
          return (sev[a.severity] || 9) - (sev[b.severity] || 9);
        }).map(a => (
          <Card key={a.id} style={{ marginBottom: 10, padding: 16, borderLeft: `3px solid ${a.severity === 'HIGH' || a.severity === 'CRITICAL' ? T.red : a.severity === 'MEDIUM' ? T.amber : T.blue}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{a.type.replace(/_/g, ' ')}</span>
                  <Badge color={a.severity === 'HIGH' || a.severity === 'CRITICAL' ? 'red' : a.severity === 'MEDIUM' ? 'amber' : 'blue'}>{a.severity}</Badge>
                </div>
                <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>
                  Customer: <span style={{ fontWeight: 500, color: T.text }}>{getCustomerName(a.customerId)}</span>
                </div>
                <div style={{ fontSize: 12, color: T.textMuted }}>{alertSummary(a)}</div>
                <div style={{ fontSize: 10, color: T.textMuted, fontFamily: mono, marginTop: 4 }}>{new Date(a.createdAt).toLocaleString()}</div>
              </div>

              {selAlert === a.id ? (
                <div style={{ minWidth: 240, marginLeft: 16 }}>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Review notes..."
                    rows={2} style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, marginBottom: 8, boxSizing: 'border-box', resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Btn small onClick={() => handleReview(a.id, AlertStatus.CLOSED_FALSE_POSITIVE)}>False Positive</Btn>
                    <Btn small variant="secondary" onClick={() => handleReview(a.id, AlertStatus.CLOSED_GENUINE)}>Genuine</Btn>
                    <Btn small variant="danger" onClick={() => handleReview(a.id, AlertStatus.ESCALATED)}>Escalate</Btn>
                    <Btn small variant="ghost" onClick={() => setSelAlert(null)}>×</Btn>
                  </div>
                </div>
              ) : (
                <Btn small variant="secondary" onClick={() => setSelAlert(a.id)}>Review</Btn>
              )}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}


// ============================================================
// CUSTOMER RISK — monitoring profiles overview
// ============================================================

function CustomerRisk({ monitoring, kyc }) {
  const customers = kyc ? kyc.getAllCustomers() : [];
  const cases = kyc ? kyc.getAllCases() : [];

  const rows = customers.map(c => {
    const kycCase = cases.find(k => k.customerId === c.id);
    const summary = monitoring.getMonitoringSummary(c.id);
    const profile = monitoring.getProfile(c.id);
    return { customer: c, kycCase, summary, profile };
  }).filter(r => r.profile); // Only show customers with monitoring profiles

  return (
    <div>
      <ST>Monitored Customers</ST>

      {rows.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 14, color: T.textSecondary }}>No customers with monitoring profiles registered.</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Profiles are registered when KYC assessment includes expected activity data.</div>
        </Card>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>{['Customer', 'Risk', 'Expected Monthly In', 'Expected Monthly Out', 'Transactions', 'Open Alerts', 'Escalated'].map((h, i) => <th key={i} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.sort((a, b) => (b.summary.alertCounts.open || 0) - (a.summary.alertCounts.open || 0)).map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{r.customer.name}</td>
                  <td style={{ padding: '8px 10px' }}><Badge color={r.profile.riskRating === 'HIGH' ? 'red' : r.profile.riskRating === 'MEDIUM' ? 'amber' : 'green'}>{r.profile.riskRating}</Badge></td>
                  <td style={{ padding: '8px 10px', fontFamily: mono }}>{r.profile.monthlyInboundValue ? fmtCcy(r.profile.monthlyInboundValue) : '—'}</td>
                  <td style={{ padding: '8px 10px', fontFamily: mono }}>{r.profile.monthlyOutboundValue ? fmtCcy(r.profile.monthlyOutboundValue) : '—'}</td>
                  <td style={{ padding: '8px 10px', fontFamily: mono, color: T.textSecondary }}>{r.summary.totalTransactions}</td>
                  <td style={{ padding: '8px 10px' }}>
                    {r.summary.alertCounts.open > 0
                      ? <Badge color="red">{r.summary.alertCounts.open}</Badge>
                      : <span style={{ color: T.green }}>✓</span>}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    {r.summary.alertCounts.escalated > 0
                      ? <Badge color="purple">{r.summary.alertCounts.escalated}</Badge>
                      : <span style={{ color: T.textMuted }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MonitoringModule;
